import { Controller } from '@hotwired/stimulus';

// Description: This controller is used to obfuscate sensitive text content with a mask character, showing only the last few characters.
// The data-obfuscation-target="field" attribute is used to specify the element containing the text to be obfuscated.
// The data-obfuscation-target="icon" attribute is used to specify an optional icon element that toggles between show/hide states.
//
// Configuration values:
// - data-obfuscation-show-chars-value: Number of characters to show at the end (default: 4)
// - data-obfuscation-mask-value: Character used for masking (default: "•")
// - data-obfuscation-show-icon-class-value: CSS class for the show icon state (default: "fa fa-eye-slash")
// - data-obfuscation-hide-icon-class-value: CSS class for the hide icon state (default: "fa fa-eye")

// Example usage (basic):
// <div data-controller="obfuscation">
//   <span data-obfuscation-target="field">1234567890</span>
//   <button data-action="click->obfuscation#toggle">
//     <i data-obfuscation-target="icon"></i>
//   </button>
// </div>

// Example usage (with custom configuration):
// <div data-controller="obfuscation"
//      data-obfuscation-show-chars-value="3"
//      data-obfuscation-mask-value="*"
//      data-obfuscation-show-icon-class-value="custom-show-icon"
//      data-obfuscation-hide-icon-class-value="custom-hide-icon">
//   <span data-obfuscation-target="field">1234567890</span>
//   <span class="clickable" data-action="click->obfuscation#toggle">
//     <i data-obfuscation-target="icon"></i>
//   </span>
// </div>

export default class extends Controller {
  static targets = ['field', 'icon'];

  static values = {
    showChars: { type: Number, default: 4 },
    mask: { type: String, default: '•' },
    showIconClass: { type: String, default: 'fa fa-eye-slash' },
    hideIconClass: { type: String, default: 'fa fa-eye' }
  }

  connect() {
    this.fieldTargets.forEach((field) => {
      field.dataset.originalText = field.textContent.trim();
      this.obfuscateField(field);
    });
    this.updateIcon(true);
  }

  toggle() {
    const field = this.fieldTarget;
    const isObfuscated = field.dataset.obfuscated === 'true';

    if (isObfuscated) {
      field.textContent = field.dataset.originalText;
      field.dataset.obfuscated = 'false';
    } else {
      this.obfuscateField(field);
    }

    this.updateIcon(!isObfuscated);
  }

  updateIcon(isObfuscated) {
    if (this.hasIconTarget) {
      this.iconTarget.className = isObfuscated ? this.hideIconClassValue : this.showIconClassValue;
    }
  }

  obfuscateField(field) {
    field.textContent = this.obfuscateText(field.dataset.originalText);
    field.dataset.obfuscated = 'true';
  }

  obfuscateText(text) {
    if (!text) return '';

    const visiblePart = text.slice(-this.showCharsValue);
    const partToMask = text.slice(0, -this.showCharsValue);

    const alphanumericRegex = /[a-zA-Z0-9]/g;
    const maskedPart = partToMask.replace(alphanumericRegex, this.maskValue);

    return maskedPart + visiblePart;
  }
}
